import { graphql } from '@apollo/client/react/hoc';
import React from 'react';
import { get as getScreen, updateScreen } from '../../../graph/screens';
import { branch, compose, mapProps, withProps, withState } from 'recompose';
import defaults from 'lodash/defaults';
import get from 'lodash/get';
import merge from 'lodash/merge';
import TextInput from '@/se/components/inputs/TextInput';
import { getNestedValue } from '@/se/utilities/data/object';
import Modal from '@/se/components/Modal';
import CheckboxInput from '../../inputs/CheckboxInput';
import { withLabel } from '@/se/components/Label';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import Form from '@/se/components/Form';
import {
  CHARTING_TABLET,
  INTERNAL_MONITOR,
  OPERATION_ROOM_MONITOR,
  PACU_TABLET,
  POST_OP_TABLET,
  REVIEW_DISPLAY,
  EXIT_DISPLAY,
  WAITING_ROOM_TABLET,
  SCHEDULE_MONITOR,
  WAITING_ROOM_MONITOR,
  ANALYTICS,
  HOSPITAL_OVERVIEW,
  PREP_TABLET,
  STAFF_MONITOR,
  PREOP_BOARD,
} from './enums';
import { CenteredSpinner } from '@/se/components/Spinner';
import PatientNameFormatInput from '../../inputs/PatientNameFormatInput';
import NumberInput from '@/se/components/inputs/NumberInput';
import RatingInput from '@/se/components/inputs/RatingInput';
import PhysicianNameFormatInput from '../../inputs/PhysicianNameFormatInput';
import SelectInput from '@/se/components/inputs/SelectInput';
import RoomMultiSelectInput from './RoomMultiSelectInput';
import RoomMultiSelectInputAll from './RoomMultiSelectInputAll';
import PatientTypeSelectInput from './PatientTypeSelectInput';
import PhysicianMultiSelectInput from './PhysicianMultiSelectInput';
import ToggleInput from '@/se/components/inputs/ToogleInput';
import { withScope } from '@/contexts/ScopeContext';
import {
  DEFAULT_SCHEDULE_COLUMN_VIEW,
  MAX_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS,
  MAX_SCHEDULE_COLUMN_MAX_COLUMNS,
  MAX_SCHEDULE_COLUMN_WIDTH_PERCENT,
  MIN_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS,
  MIN_SCHEDULE_COLUMN_MAX_COLUMNS,
  MIN_SCHEDULE_COLUMN_WIDTH_PERCENT,
} from '@/components/pages/kiosk/monitor/scheduleColumn.constants';
import {
  clampCarouselIntervalSeconds,
  clampMaxColumns,
  clampWidthPercent,
} from '@/components/pages/kiosk/monitor/scheduleColumnConfig';
import { DEFAULT_PATIENT_CARD_SORT_ORDER } from '@/components/patient/PatientSlot/patientCardConfig';

const whenScheduleColumnEnabled = branch(
  ({ rootValue }) => !rootValue?.showScheduleColumn,
  () => () => null
);

const whenScheduleColumnCarouselEnabled = branch(
  ({ rootValue }) => !rootValue?.showScheduleColumn || rootValue?.scheduleColumnView !== 'CAROUSEL',
  () => () => null
);

const DescriptionInput = compose(
  withLabel('Monitor Description'),
  withProps({
    placeholder: 'e.g., By the corridor',
    autoComplete: 'off',
    required: false,
  })
)(TextInput);

const createPatientCardSettingsInput = (extraSchema = {}) =>
  compose(
    withLabel('Patient Card Settings', { section: true }),
    withProps({
      schema: {
        patientCardShowAgeAndSex: withProps({ label: 'Show Patient Age and Sex' })(CheckboxInput),
        patientCardShowProcedureTimes: withProps({ label: 'Show Scheduled Procedure Start and End Time' })(
          CheckboxInput
        ),
        ...extraSchema,
        patientCardSortOrder: compose(
          withLabel('Patient Sort Order'),
          withProps({
            options: {
              DEFAULT: 'Default',
              SCHEDULED_START_ASC: 'Scheduled Procedure Start Time — Earliest First',
              PHYSICIAN_NAME_ASC: 'Physician Name — A to Z',
            },
            isClearable: false,
            required: true,
          })
        )(SelectInput),
      },
    })
  )(ObjectInput);

const PatientCardSettingsInput = createPatientCardSettingsInput();
const ScheduledOrPatientCardSettingsInput = createPatientCardSettingsInput({
  patientCardShowScheduledOr: withProps({ label: 'Show Scheduled OR' })(CheckboxInput),
});

const OriginalPacuPatientCardSettingsInput = branch(
  ({ rootValue }) => Number(rootValue?.screenVersion) === 1,
  () => () => null
)(PatientCardSettingsInput);

const InternalMonitorConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    sound: compose(
      withLabel('Sound Settings', { section: true }),
      withProps({
        schema: {
          prepReadySound: withProps({ label: 'Play sound when patient is in Waiting Room and ready for PreOp' })(
            CheckboxInput
          ),
          orReadySound: withProps({ label: 'Play sound when patient is in PreOp and ready for OR' })(CheckboxInput),
          readyForSurgeonSound: withProps({ label: 'Play sound when patient is in PreOp and ready for surgeon' })(
            CheckboxInput
          ),
          preOpBoardSurgeonReadySound: withProps({ label: 'Play sound when PreOp Board S-Ready turns green' })(
            CheckboxInput
          ),
          holdProcedureSound: withProps({ label: 'Play sound when the procedure is on hold' })(CheckboxInput),
          blockNerveSound: withProps({ label: 'Play sound when the patient nerve is blocked by anesthesia' })(
            CheckboxInput
          ),
          // Sound config for both Recovery and Post-Op room
          pacuReadySound: withProps({ label: 'Play sound when patient is ready to see family' })(CheckboxInput),
          waitingReadySound: withProps({ label: 'Play sound when the front desk hits Family Here' })(CheckboxInput),
          readyToPickupSound: withProps({
            label: 'Play sound when caretaker message for pick up confirmation is received',
          })(CheckboxInput),
          dischargeSound: withProps({ label: 'Play sound when patient is Discharged' })(CheckboxInput),
          procedureEndSound: withProps({ label: 'Play sound when procedure ends' })(CheckboxInput),
          closingSound: withProps({ label: 'Play sound when closing starts' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          showProcedure: withProps({ label: 'Display procedure type on screen' })(CheckboxInput),
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    physician: compose(
      withLabel('Physician Settings', { section: true }),
      withProps({
        schema: {
          physicianNameFormat: withLabel('Physician Name Format')(PhysicianNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    screenFocus: compose(
      withLabel('Select Screens Settings', { section: true }),
      withProps({
        schema: {
          preOpFocused: withProps({ label: 'PreOp Focused View' })(CheckboxInput),
          pacuFocused: withProps({ label: 'Pacu Focused View' })(CheckboxInput),
          messagingScreen: withProps({ label: 'Messaging View' })(CheckboxInput),
          expandedOrTileView: withProps({ label: 'Expanded OR Tile View' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    schedule: compose(
      withLabel('Scheduled Patients Settings', { section: true }),
      withProps({
        schema: {
          showScheduleColumn: withProps({ label: 'Show Scheduled Patients' })(CheckboxInput),
          scheduleColumnView: compose(
            whenScheduleColumnEnabled,
            withLabel('Scheduled Patients View'),
            withProps({
              options: {
                LIST: 'List (next patients first)',
                CAROUSEL: 'Carousel (auto-rotating pages)',
              },
              required: false,
            })
          )(SelectInput),
          scheduleColumnCarouselIntervalSeconds: compose(
            whenScheduleColumnCarouselEnabled,
            withLabel('Carousel Rotation Interval (seconds)'),
            withProps({
              min: MIN_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS,
              max: MAX_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS,
              step: 1,
              required: false,
            })
          )(NumberInput),
          scheduleColumnWidth: compose(
            whenScheduleColumnEnabled,
            withLabel('Scheduled Patients Column Width (% of screen)'),
            withProps({
              min: MIN_SCHEDULE_COLUMN_WIDTH_PERCENT,
              max: MAX_SCHEDULE_COLUMN_WIDTH_PERCENT,
              required: false,
            })
          )(NumberInput),
          scheduleColumnMaxColumns: compose(
            whenScheduleColumnEnabled,
            withLabel('Max Schedule Columns'),
            withProps({
              min: MIN_SCHEDULE_COLUMN_MAX_COLUMNS,
              max: MAX_SCHEDULE_COLUMN_MAX_COLUMNS,
              required: false,
            })
          )(NumberInput),
          scheduleColumnGroupByTime: compose(
            whenScheduleColumnEnabled,
            withProps({ label: 'Group By Time Slot' })
          )(CheckboxInput),
        },
      })
    )(ObjectInput),
    patientCards: ScheduledOrPatientCardSettingsInput,
    showOrRooms: withLabel('Show Rooms')(RoomMultiSelectInputAll),
    showPatientTypes: withLabel('Show Patient Types')(PatientTypeSelectInput),
    showPhysicians: withLabel('Show Physicians')(PhysicianMultiSelectInput),
    showPostOp: compose(
      withScope,
      branch(
        ({ scope }) => !scope.hospital?.modules?.hasPostop,
        () => () => null
      ),
      withProps({ label: 'Show PostOP' }),
      mapProps(({ value, ...props }) => ({ value: value !== false, ...props }))
    )(CheckboxInput),
    lightMode: withProps({ label: 'Light Mode Internal' })(ToggleInput),
    showScheduledProcedureDuration: withProps({ label: 'Show Scheduled Procedure Duration' })(ToggleInput),
    showLatePreOpIndicator: withProps({ label: 'Show PreOP Late Indicator' })(ToggleInput),
    showStaffIndicators: withProps({ label: 'Show Staff Tracking Indicators' })(ToggleInput),
    showPatientNotesOnInternalScreen: withProps({ label: 'Show Patient Notes On Internal Screen' })(ToggleInput),
  },
  flatten: {
    sound: true,
    privacy: true,
    physician: true,
    notifications: true,
    screenFocus: true,
    schedule: true,
    patientCards: true,
  },
})(ObjectInput);

const OperationRoomConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    features: compose(
      withLabel('Features', { section: true }),
      withProps({
        schema: {
          enableFlipRoom: withProps({ label: 'Enable Flip Room' })(CheckboxInput),
          enableHelp: withProps({ label: 'Enable Help' })(CheckboxInput),
          enabledReturnPatient: withProps({ label: 'Enable Return Patient' })(CheckboxInput),
          showProcedureDelayReasonModal: withProps({ label: 'Show Procedure Delay Reason Modal' })(CheckboxInput),
          // Temporary: hides the "Patient not Detected? Tap Here." button on the OR iPad. Remove in the future.
          hidePatientNotDetected: withProps({ label: 'Hide Patient Not Detected Button' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    privacy: true,
    notifications: true,
    features: true,
  },
})(ObjectInput);

const WaitingRoomConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    sound: compose(
      withLabel('Sound Settings', { section: true }),
      withProps({
        schema: {
          // Sound config for both Recovery and Post-Op room
          pacuReadySound: withProps({ label: 'Play sound when patient is ready to see family' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    useCssGridLayout: compose(
      withLabel('Implementation Settings', { section: true }),
      withProps({
        schema: {
          useCssGridLayout: withProps({ label: 'Use CSS grid layout' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    sound: true,
    privacy: true,
    notifications: true,
    useCssGridLayout: true,
  },
})(ObjectInput);

const ScheduleConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    isTimeline: withProps({ label: 'Show schedule in Timeline view' })(CheckboxInput),
    anesthesiologistSchedule: withProps({ label: 'Anesthesiologist Schedule' })(CheckboxInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    showBedNumber: withProps({ label: 'Show bed number' })(CheckboxInput),
    showStaffListOnSchedule: withProps({ label: 'Show staff list on Schedule' })(CheckboxInput),
    showDatePickerOnSchedule: withProps({ label: 'Show date picker' })(CheckboxInput),
    showOrRooms: withLabel('Show OR Rooms')(RoomMultiSelectInput),
    lightMode: withProps({ label: 'Light Mode Schedule' })(ToggleInput),
    compactInitials: withProps({ label: 'Compact Initials' })(CheckboxInput),
    wrapProcedureName: withProps({ label: 'Wrap Procedure Name' })(CheckboxInput),
    highlightNotes: withProps({ label: 'Highlight Notes' })(CheckboxInput),
    hideNotes: withProps({ label: 'Hide Notes' })(CheckboxInput),
    highlightAllergies: withProps({ label: 'Highlight Allergies' })(CheckboxInput),
    timelineViewRoomTime: withProps({ label: 'Show Room Time in Timeline View' })(CheckboxInput),
  },
  flatten: {
    privacy: true,
    notifications: true,
  },
})(ObjectInput);

const PreOpConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    physician: compose(
      withLabel('Physician Settings', { section: true }),
      withProps({
        schema: {
          physicianNameFormat: withLabel('Physician Name Format')(PhysicianNameFormatInput),
        },
      })
    )(ObjectInput),
    showPatientTypes: withLabel('Show Patient Types')(PatientTypeSelectInput),
    showPhysicians: withLabel('Show Physicians')(PhysicianMultiSelectInput),
    patientCards: ScheduledOrPatientCardSettingsInput,
    showLatePreOpIndicator: withProps({ label: 'Show PreOP Late Indicator' })(ToggleInput),
    // Same persisted flag as the OR tablet's delay reason prompt, so the server
    // agrees with this screen when it decides a first case is delayed.
    showProcedureDelayReasonModal: withProps({
      label: 'Enable OR Delay Reason (first case tag and Delay Reason option)',
    })(CheckboxInput),
    dischargeModalV2: withProps({ label: 'Use V2 Discharge Modal' })(CheckboxInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    sound: true,
    privacy: true,
    physician: true,
    notifications: true,
    patientCards: true,
  },
})(ObjectInput);

const PacuConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    sound: compose(
      withLabel('Sound Settings', { section: true }),
      withProps({
        schema: {
          readyToPickupSound: withProps({
            label: 'Play sound when caretaker message for pick up confirmation is received',
          })(CheckboxInput),
        },
      })
    )(ObjectInput),
    version: compose(
      withLabel('Screen Variant', { section: true }),
      withProps({
        schema: {
          screenVersion: compose(
            withLabel('UI version'),
            withProps({
              options: { 0: 'Original', 1: 'V2' },
              name: 'screenVersion',
              defaultValue: 0,
              isClearable: false,
              required: true,
            })
          )(SelectInput),
          orthopedic: withProps({ label: 'Optimize for orthopedic practice' })(CheckboxInput),
          dischargeModalV2: withProps({ label: 'Use V2 Discharge Modal' })(CheckboxInput),
          showDelayReasons: withProps({ label: 'Show Delay Reasons' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    physician: compose(
      withLabel('Physician Settings', { section: true }),
      withProps({
        schema: {
          physicianNameFormat: withLabel('Physician Name Format')(PhysicianNameFormatInput),
        },
      })
    )(ObjectInput),
    showPatientTypes: withLabel('Show Patient Types')(PatientTypeSelectInput),
    showPhysicians: withLabel('Show Physicians')(PhysicianMultiSelectInput),
    patientCards: OriginalPacuPatientCardSettingsInput,
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    sound: true,
    version: true,
    privacy: true,
    physician: true,
    notifications: true,
    patientCards: true,
  },
})(ObjectInput);

const PostOpConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    sound: compose(
      withLabel('Sound Settings', { section: true }),
      withProps({
        schema: {
          readyToPickupSound: withProps({
            label: 'Play sound when caretaker message for pick up confirmation is received',
          })(CheckboxInput),
        },
      })
    )(ObjectInput),
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    physician: compose(
      withLabel('Physician Settings', { section: true }),
      withProps({
        schema: {
          physicianNameFormat: withLabel('Physician Name Format')(PhysicianNameFormatInput),
        },
      })
    )(ObjectInput),
    showPatientTypes: withLabel('Show Patient Types')(PatientTypeSelectInput),
    showPhysicians: withLabel('Show Physicians')(PhysicianMultiSelectInput),
    patientCards: PatientCardSettingsInput,
    dischargeModalV2: withProps({ label: 'Use V2 Discharge Modal' })(CheckboxInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    sound: true,
    privacy: true,
    physician: true,
    notifications: true,
    patientCards: true,
  },
})(ObjectInput);

const ChartingConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    chartingPinTimeout: compose(
      withScope,
      branch(
        ({ scope }) => !scope.hospital?.modules?.patientCharting,
        () => () => null
      ),
      withLabel('Charting PIN Timeout'),
      withProps({
        options: {
          30: '30 seconds',
          60: '1 minute',
          120: '2 minutes',
          180: '3 minutes',
          240: '4 minutes',
          300: '5 minutes',
          null: 'Never',
        },
        name: 'chartingPinTimeout',
        isClearable: false,
      }),
      mapProps(({ value, onChange, ...props }) => ({
        value: value === null ? 'null' : String(value),
        onChange: value => {
          onChange(value === 'null' ? null : Number(value));
        },
        ...props,
      }))
    )(SelectInput),
  },
  flatten: {
    privacy: true,
    notifications: true,
  },
})(ObjectInput);

const ReviewsConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
    limit: compose(
      withLabel('Max. Number of Reviews', { section: true }),
      withProps({
        min: 1,
        required: true,
        style: {
          marginBottom: '1rem',
        },
      })
    )(NumberInput),
    minimumRating: compose(
      withLabel('Minimum Rating', { section: true }),
      withProps({
        style: {
          marginBottom: '2rem',
        },
      })
    )(RatingInput),
  },
  flatten: {
    privacy: true,
    notifications: true,
  },
})(ObjectInput);

const ExitDisplayConfigInput = withProps({
  schema: {
    description: DescriptionInput,
  },
})(ObjectInput);

const WaitingRoomTabletConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    questionnaireSessionTimeout: compose(
      withLabel('Questionnaire & Registration Package Timeout'),
      withProps({
        options: {
          30: '30 seconds',
          60: '1 minute',
          120: '2 minutes',
          180: '3 minutes',
          240: '4 minutes',
          300: '5 minutes',
          null: 'Never',
        },
        name: 'questionnaireSessionTimeout',
        isClearable: false,
      }),
      mapProps(({ value, onChange, ...props }) => ({
        value: value === null ? 'null' : String(value),
        onChange: value => {
          onChange(value === 'null' ? null : Number(value));
        },
        ...props,
      }))
    )(SelectInput),
    showRideHome: withProps({ label: 'Show Ride Home' })(CheckboxInput),
    showCaretakerWr: withProps({ label: 'Show Caretaker Information' })(CheckboxInput),
    showPreOpWr: withProps({ label: 'Show PreOp Questionnaire' })(CheckboxInput),
    showIntakeWr: withProps({ label: 'Show Registration Package Questionnaire' })(CheckboxInput),
  },
})(ObjectInput);

const ConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    privacy: compose(
      withLabel('Patient Privacy Settings', { section: true }),
      withProps({
        schema: {
          patientNameFormat: withLabel('Patient Name Format')(PatientNameFormatInput),
        },
      })
    )(ObjectInput),
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    privacy: true,
    notifications: true,
  },
})(ObjectInput);

const PreOpBoardConfigInput = withProps({
  schema: {
    description: DescriptionInput,
    notifications: compose(
      withLabel('Notification Settings', { section: true }),
      withProps({
        schema: {
          sendNotifications: withProps({ label: 'Send notifications about screen connectivity status' })(CheckboxInput),
        },
      })
    )(ObjectInput),
  },
  flatten: {
    notifications: true,
  },
})(ObjectInput);

const inputForScreenType = screenType => {
  switch (screenType) {
    case INTERNAL_MONITOR:
      return InternalMonitorConfigInput;
    case OPERATION_ROOM_MONITOR:
      return OperationRoomConfigInput;
    case WAITING_ROOM_MONITOR:
      return WaitingRoomConfigInput;
    case SCHEDULE_MONITOR:
      return ScheduleConfigInput;
    case PREP_TABLET:
      return PreOpConfigInput;
    case PACU_TABLET:
      return PacuConfigInput;
    case POST_OP_TABLET:
      return PostOpConfigInput;
    case CHARTING_TABLET:
      return ChartingConfigInput;
    case REVIEW_DISPLAY:
      return ReviewsConfigInput;
    case EXIT_DISPLAY:
      return ExitDisplayConfigInput;
    case WAITING_ROOM_TABLET:
      return WaitingRoomTabletConfigInput;
    case ANALYTICS:
      return ConfigInput;
    case HOSPITAL_OVERVIEW:
      return ConfigInput;
    case STAFF_MONITOR:
      return ConfigInput;
    case PREOP_BOARD:
      return PreOpBoardConfigInput;
    default:
      return ConfigInput;
  }
};

const defaultPatientNameFormatForScreenType = screenType => {
  switch (screenType) {
    case OPERATION_ROOM_MONITOR:
    case PREOP_BOARD:
      return 'FullName';
    default:
      return 'InitialsOnly';
  }
};

const ScreenConfig = ({ baseUrl, history, match, updateScreen, busy, setBusy, data }) => {
  const handleSubmit = async value => {
    setBusy(true);
    try {
      const code = getNestedValue('params.id', match);
      const preparedValue = defaults(value, {
        showProcedure: false,
        patientNameFormat: defaultPatientNameFormatForScreenType(screenType),
        sendNotifications: true,
      });
      await updateScreen({
        variables: {
          screenCode: code,
          ...preparedValue,
          showPostOp: value.showPostOp !== false,
          screenVersion: parseInt(preparedValue.screenVersion || '0', 10),
        },
      });
      history.push(baseUrl);
    } finally {
      setBusy(false);
    }
  };

  const loading = getNestedValue('loading', data);
  const screenType = getNestedValue('screen.connection.type.type', data);
  const Input = inputForScreenType(screenType);
  const preparedData = merge(
    {},
    {
      patientNameFormat: defaultPatientNameFormatForScreenType(screenType),
      limit: 10,
      minimumRating: 4,
      sendNotifications: false,
    },
    get(data, 'screen.configuration')
  );
  // Stored configs carry null for never-set fields, which merge would keep — apply
  // the schedule column defaults on top so the form pre-selects them.
  preparedData.scheduleColumnView = preparedData.scheduleColumnView ?? DEFAULT_SCHEDULE_COLUMN_VIEW;
  preparedData.scheduleColumnWidth = clampWidthPercent(preparedData.scheduleColumnWidth);
  preparedData.scheduleColumnMaxColumns = clampMaxColumns(preparedData.scheduleColumnMaxColumns);
  preparedData.scheduleColumnGroupByTime = preparedData.scheduleColumnGroupByTime ?? false;
  preparedData.scheduleColumnCarouselIntervalSeconds = clampCarouselIntervalSeconds(
    preparedData.scheduleColumnCarouselIntervalSeconds
  );
  preparedData.patientCardShowAgeAndSex = preparedData.patientCardShowAgeAndSex ?? false;
  preparedData.patientCardShowProcedureTimes = preparedData.patientCardShowProcedureTimes ?? false;
  preparedData.patientCardShowScheduledOr = preparedData.patientCardShowScheduledOr ?? false;
  preparedData.patientCardSortOrder = preparedData.patientCardSortOrder ?? DEFAULT_PATIENT_CARD_SORT_ORDER;
  preparedData.preOpBoardSurgeonReadySound = preparedData.preOpBoardSurgeonReadySound ?? false;

  return (
    <Modal title="Configure Screen" closeTo={baseUrl}>
      {!loading ? (
        <Form autoFocus initialValue={preparedData} input={Input} label={'Save'} onSubmit={handleSubmit} />
      ) : (
        <CenteredSpinner />
      )}
    </Modal>
  );
};

export default compose(
  withState('busy', 'setBusy', false),
  graphql(updateScreen, { name: 'updateScreen' }),
  graphql(getScreen, { options: ({ match }) => ({ variables: { screenCode: getNestedValue('params.id', match) } }) })
)(ScreenConfig);
